import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";
import useOptions from "features/masterData/hooks/useOptions";
import { deleteKeysByList, generateRandomString } from "utils/helper";
import { PAGE_TYPE } from "../schemas/incomeDeductionSchema";
import * as services from "../service/incomeDeductionApi";
import * as services2 from "../../requestApproval/service/pendingApprovalApi";

import {
  setCurrentIncomeDeduction,
  setIsLoading,
} from "../slices/payrollSlice";
import { useTranslation } from "react-i18next";
import { getFundList } from "features/fund/services/fundApi";

function toInitialValues(initialValues = {}) {
  const {
    taxCalculation = {},
    frequencyOfPayment = {},
    incomeType = {},
    rounding = {},
    ...rest
  } = initialValues;
  const modifiedValues = {
    ...rest,
    taxCalculation: taxCalculation?.id,
    rounding: rounding?.id,
    incomeType: incomeType?.id,
    frequencyOfPayment: frequencyOfPayment?.id,
    providentFund: initialValues?.providentFund === "Y",
    socialSecurityFund: initialValues?.socialSecurityFund === "Y",
    offPeriod: initialValues?.offPeriod === "Y",
    leaveWithoutPay: initialValues?.leaveWithoutPay === "Y",
    previousPeriod: initialValues?.previousPeriod === "Y",
  };
  return modifiedValues;
}

function toParams(params = {}) {
  let modifiedParams = {
    ...params,
    incomeDeductionId: params.incomeDeducationId,
    incomeDeductinType: params.incomeType,
    calculateOnce: params.calculateOnce ? "Y" : "N",
    providentFund: params.providentFund ? "Y" : "N",
    socialSecurityFund: params.socialSecurityFund ? "Y" : "N",
    type: PAGE_TYPE.DEDUCTION,
    offPeriod: params.offPeriod ? "Y" : "N",
    leaveWithoutPay: params.leaveWithoutPay ? "Y" : "N",
    previousPeriod: params.previousPeriod ? "Y" : "N"
  };
  // return deleteKeysByList(modifiedParams, ["offPeriod", "previousPeriod"]);
  return modifiedParams;
}

function useDeduction() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.payroll.isLoading);
  const currentIncomeDeduction = useSelector(
    (state) => state.payroll.currentIncomeDeduction
  );
  const [fundOptions, setFundOptions] = useState([])
  const [leaveGroupOptions, setLeaveGroupOptions] = useState([]);
  console.log("leaveGroupOptions", leaveGroupOptions);

  const {
    incomeDeductionByTypeOptions = [],
    roundingTypeOptions = [],
    frequencyOfPaymentOptions = [],
    taxCalculationOptions = [],
    // leaveGroupOptions = [],
    // getLeaveGroupOptions,
    getIncomeDeductionByTypeOptions,
    getRoundingTypeOptions,
    getFrequencyOfPaymentOptions,
    getTaxCalculationOptions,
  } = useOptions();

  const fetchFundOptions = useCallback(async () => {
    try {
      const response = await getFundList({
        requestId: generateRandomString(),
        companyId: currentCompany,
        page: 0,
        size: 200,
      });
      const list = response?.data?.content || [];
      console.log("list", list);

      setFundOptions(
        list.map((f) => ({
          value: f.id,
          label: i18n.language === "en" ? f.fundNameEn : f.fundNameTh,
          fundCode: f.fundCode,
          securitiesCompany: f.securitiesCompany,
          ...f
        }))
      );
    } catch (error) {
      setFundOptions([]);
    }
  }, [currentCompany, i18n.language]);

  useEffect(() => {
    fetchFundOptions();
  }, [fetchFundOptions]);

  const getLeaveGroupOptions = useCallback(async () => {
    if (typeof currentCompany !== "object") {
      try {
        dispatch(setIsLoading(true));
        const values = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
        };
        const response = await services2.getLeaveCategoryGroupIsLeaveApprovalAllActive(values);

        const modifiedOptions = response?.data?.map((opt) => ({
          ...opt,
          label: i18n.language == "en" ? opt.nameEn : opt.name,
          value: opt.id,
        }));
        setLeaveGroupOptions(modifiedOptions);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  }, [currentCompany, i18n.language, dispatch]);


  const initialValues = useMemo(() => {
    return toInitialValues(currentIncomeDeduction);
  }, [currentIncomeDeduction]);

  const getDeduction = useCallback(
    async (incomeId) => {
      try {
        dispatch(setIsLoading(true));
        if (incomeId) {
          const values = {
            incomeDeductionId: incomeId,
            requestId: generateRandomString(),
            companyId: currentCompany,
            type: PAGE_TYPE.DEDUCTION,
          };
          const response = await services.getIncomeDeduction(values);
          dispatch(setCurrentIncomeDeduction(response.data));
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const createDeduction = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.createIncomeDeduction(toParams(values));
        message.success(t("common.success"))
        navigate("/configuration/payroll", { state: { selectedTab: 'deduction' } });
        dispatch(setCurrentIncomeDeduction({}));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const updateDeduction = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateIncomeDeduction(toParams(values));
        message.success(t("common.success"))
        getDeduction(params.id);
        navigate("/configuration/payroll", { state: { selectedTab: 'deduction' } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = {
        ...values,
        incomeDeductionId: values.incomeDeducationId,
        companyId: currentCompany,
      };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {i18n.language == "en"
                ? `Are you sure confirm to ${values.incomeDeducationId ? "edit" : "create"} Deduction ?`
                : `คุณยืนยันการ${values.incomeDeducationId ? "แก้ไข" : "สร้าง"}รายหักหรือไม่ ?`}
            </p>
          </>
        ),
        onOk() {
          if (values.incomeDeducationId) {
            updateDeduction(modifiedValues);
          } else {
            createDeduction(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [currentCompany, updateDeduction, createDeduction, t, i18n]
  );

  const onCancel = useCallback(() => {
    navigate("/configuration/payroll", { state: { selectedTab: 'deduction' } });
  }, [navigate]);

  useEffect(() => {
    if (typeof currentCompany !== "object" && params.id) {
      getDeduction(params.id);
    }
  }, [currentCompany, params.id, getDeduction]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getIncomeDeductionByTypeOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        type: PAGE_TYPE.DEDUCTION,
      });
      getLeaveGroupOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        // type: PAGE_TYPE.DEDUCTION,
      })
    }
  }, [currentCompany, getIncomeDeductionByTypeOptions]);

  useEffect(() => {
    getRoundingTypeOptions();
  }, [getRoundingTypeOptions]);

  useEffect(() => {
    getFrequencyOfPaymentOptions();
  }, [getFrequencyOfPaymentOptions]);

  useEffect(() => {
    getTaxCalculationOptions();
  }, [getTaxCalculationOptions]);

  return {
    isLoading,
    initialValues,
    roundingTypeOptions,
    leaveGroupOptions,
    incomeDeductionByTypeOptions,
    fundOptions,
    frequencyOfPaymentOptions,
    taxCalculationOptions,
    onSubmit,
    onCancel,
  };
}

export default useDeduction;
